import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Alert, Button, ListGroup, Spinner } from 'react-bootstrap';
import { FaShoppingBag, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import { orderService } from '../services/api';

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Fetch order details from API
    const fetchOrder = async () => {
      try {
        setLoading(true);
        
        if (orderId) {
          try {
            // Try to fetch the real order using the service
            const response = await orderService.getOrderById(orderId);
            setOrder(response.data);
            console.log('Order data:', response.data);
          } catch (apiError) {
            console.error('API Error:', apiError);
            // If API fails, fall back to mock data
            createMockOrder();
          }
        } else {
          // No order ID, use mock data
          createMockOrder();
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    const createMockOrder = () => {
      // Mock order data
      const mockOrder = {
        id: orderId || 'ORD123456789',
        date: new Date().toISOString(),
        status: 'Processing',
        items: [
          { id: 1, title: 'Smartphone X', price: 999.99, quantity: 1, image: 'https://via.placeholder.com/80' },
          { id: 2, title: 'Wireless Earbuds', price: 149.99, quantity: 2, image: 'https://via.placeholder.com/80' }
        ],
        shippingAddress: {
          firstName: 'John',
          lastName: 'Doe',
          address: '123 Main Street',
          city: 'San Francisco',
          state: 'CA',
          zipCode: '94105',
          country: 'United States'
        },
        paymentMethod: 'Razorpay',
        paymentId: 'pay_' + Math.random().toString(36).substr(2, 9),
        subtotal: 1299.97,
        shipping: 0.00,
        tax: 103.99,
        total: 1413.96
      };
      
      setOrder(mockOrder);
    };
    
    fetchOrder();
  }, [orderId]);

  const formatDate = (dateString) => {
    try {
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (e) {
      return 'Invalid date';
    }
  };

  const formatAddress = (address) => {
    if (typeof address === 'string') {
      // If address is a string, just return it
      return address;
    } else if (address && typeof address === 'object') {
      // If address is an object, format it nicely
      const {
        firstName,
        lastName,
        address: streetAddress,
        city,
        state,
        zipCode,
        country
      } = address;
      
      return (
        <>
          {firstName} {lastName}<br />
          {streetAddress}<br />
          {city}, {state} {zipCode}<br />
          {country}
        </>
      );
    }
    
    return 'Address not available';
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading order details...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-center">
            <Link to="/" className="btn btn-outline-primary">
              Return to Home
            </Link>
          </div>
        </Alert>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <Alert.Heading>Order Not Found</Alert.Heading>
          <p>We couldn't find the order you're looking for.</p>
          <hr />
          <div className="d-flex justify-content-center">
            <Link to="/" className="btn btn-outline-primary">
              Return to Home
            </Link>
          </div>
        </Alert>
      </Container>
    );
  }

  // Handle either order structure (API or mock)
console.log(order, "order");
  const orderItems = order.items || [];
  const orderDate = order.date || order.created_at || new Date().toISOString();
  const orderStatus = order.order.status || 'Processing';
  const paymentMethod = order.order.paymentMethod || 'Online Payment';
const orderTotal = order.order.total_amount || 0;
  const subtotal = order.order.total_amount || (orderTotal * 0.9) || 0;
  const shipping = order.order.shipping || 0;
  const tax = order.order.tax || (orderTotal * 0.08) || 0;


  return (
    <Container className="py-5">
      <Row className="mb-4">
        <Col>
          <Alert variant="success" className="d-flex align-items-center">
            <FaCheckCircle className="me-3 fs-3" />
            <div>
              <h4 className="alert-heading mb-1">Thank You for Your Order!</h4>
              <p className="mb-0">Your order #{order.id} has been placed and is being processed.</p>
            </div>
          </Alert>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="bg-white">
              <h4 className="mb-0">Order Details</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6} className="mb-3 mb-md-0">
                  <p><strong>Order Number:</strong> {order.id}</p>
                  <p><strong>Order Date:</strong> {formatDate(orderDate)}</p>
                  <p><strong>Order Status:</strong> <span className="badge bg-success">{orderStatus}</span></p>
                  <p><strong>Payment Method:</strong> {paymentMethod}</p>
                  {order.paymentId && (
                    <p><strong>Payment ID:</strong> {order.paymentId}</p>
                  )}
                </Col>
                <Col md={6}>
                  <h5>Shipping Address</h5>
                  <address>
                    {formatAddress(JSON.parse(order.order.shipping_address))}
                  </address>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="bg-white">
              <h4 className="mb-0">Order Items</h4>
            </Card.Header>
            <Card.Body className="p-0">
              <ListGroup variant="flush">
                {orderItems.map((item) => (
                  <ListGroup.Item key={item.id} className="py-3">
                    <Row className="align-items-center">
                      <Col xs={2} md={1}>
                        <img 
                          src={item.image || item.image_url || "https://via.placeholder.com/80"} 
                          alt={item.title || item.name}
                          className="img-fluid" 
                          style={{ maxHeight: '60px', objectFit: 'contain' }}
                        />
                      </Col>
                      <Col xs={6} md={7}>
                        <h6 className="mb-0">{item.title || item.name}</h6>
                        <small className="text-muted">Quantity: {item.quantity}</small>
                      </Col>
                      <Col xs={4} md={4} className="text-end">
                        <span className="fw-bold">₹{((typeof item.price === 'number' ? item.price : parseFloat(item.price || 0)) * item.quantity).toFixed(2)}</span>
                        <small className="text-muted d-block">
                        ₹{(typeof item.price === 'number' ? item.price : parseFloat(item.price || 0)).toFixed(2)} each
                        </small>
                      </Col>
                    </Row>
                  </ListGroup.Item>
                ))}
              </ListGroup>
              
              <div className="p-3 border-top">
                <Row className="mb-0">
                  <Col md={8}>
                    <div className="d-flex justify-content-md-end justify-content-start text-md-end mb-3 mb-md-0">
                      <dl className="mb-0">
                        <dt className="text-muted me-3">Subtotal:</dt>
                        <dd className="text-muted me-3">Shipping:</dd>
                        <dt className="text-muted me-3">Tax:</dt>
                        <dt className="fw-bold me-3">Total:</dt>
                      </dl>
                      <dl className="mb-0">
                        <dd className="text-muted">₹{(typeof subtotal === 'number' ? subtotal : parseFloat(subtotal || 0)).toFixed(2)}</dd>
                        <dd className="text-muted">₹{(typeof shipping === 'number' ? shipping : parseFloat(shipping || 0)).toFixed(2)}</dd>
                        <dd className="text-muted">₹{(typeof tax === 'number' ? tax : parseFloat(tax || 0)).toFixed(2)}</dd>
                        <dd className="fw-bold">₹{(typeof orderTotal === 'number' ? orderTotal : parseFloat(orderTotal || 0)).toFixed(2)}</dd>
                      </dl>
                    </div>
                  </Col>
                </Row>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <h5>What's Next?</h5>
              <p>You will receive an order confirmation email with details of your order. Once your order ships, you will receive tracking information.</p>
              <p className="mb-0">If you have any questions or concerns about your order, please <Link to="/contact">contact our customer service team</Link>.</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <div className="d-flex justify-content-between">
        <Link to="/products" className="btn btn-outline-primary">
          <FaArrowLeft className="me-2" /> Continue Shopping
        </Link>
        <Button variant="outline-secondary" onClick={() => window.print()}>
          Print Order
        </Button>
      </div>
    </Container>
  );
};

export default OrderConfirmationPage; 